import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import Dashboard from './Dashboard';
import './DeliveryDispatch.css';

const BASE_URL = process.env.REACT_APP_API_URL || 'https://vakra.co.in';
const DELIVERY_API = `${BASE_URL}/api/delivery-dispatch`;
const STANDARD_ORDERS_API = `${BASE_URL}/api/standard-orders`;
const GROCERY_ORDERS_API = `${BASE_URL}/api/grocery-orders`;

const emptyRiderForm = {
  name: '',
  mobile: '',
  pin: '',
  vehicleType: 'bike',
  vehicleNumber: '',
  serviceArea: '',
  status: 'available',
  notes: '',
};

const statusLabel = {
  available: 'Available',
  busy: 'Busy',
  on_delivery: 'On Delivery',
  inactive: 'Inactive',
  offline: 'Offline',
};

const tripStatusLabel = {
  assigned: 'Assigned',
  picked_up: 'Picked Up',
  out_for_delivery: 'Out for Delivery',
  delivered: 'Delivered',
  cancelled: 'Cancelled',
};

const DeliveryDispatch = ({
  embedded = false,
  externalVendorId = null,
  externalVendorType = 'fastfood',
  onBack = null,
}) => {
  const [vendorId, setVendorId] = useState(externalVendorId || null);
  const [vendorType, setVendorType] = useState(
    String(externalVendorType || 'fastfood').toLowerCase()
  );

  const [readyOrders, setReadyOrders] = useState([]);
  const [riders, setRiders] = useState([]);
  const [activeTrips, setActiveTrips] = useState([]);
  const [tripHistory, setTripHistory] = useState([]);
  const [riderRecords, setRiderRecords] = useState([]);
  const [selectedRiderRecord, setSelectedRiderRecord] = useState(null);

  const [selectedOrderIds, setSelectedOrderIds] = useState([]);
  const [selectedRiderId, setSelectedRiderId] = useState('');
  const [selectedArea, setSelectedArea] = useState('all');
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState('dispatch');
  const [trackingTripId, setTrackingTripId] = useState(null);

  const [showRiderForm, setShowRiderForm] = useState(false);
  const [riderForm, setRiderForm] = useState(emptyRiderForm);

  const [resetPinRider, setResetPinRider] = useState(null);
  const [resetPinValue, setResetPinValue] = useState('');
  const [deleteRiderTarget, setDeleteRiderTarget] = useState(null);

  const [reassignOrder, setReassignOrder] = useState(null);
  const [reassignRiderId, setReassignRiderId] = useState('');
  const [reassignReason, setReassignReason] = useState('');

  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('success');

  const getToken = () => localStorage.getItem('token') || '';

  const showMessage = useCallback((text, type = 'success') => {
    setMessage(text);
    setMessageType(type);
    window.clearTimeout(showMessage._timer);
    showMessage._timer = window.setTimeout(() => setMessage(''), 3200);
  }, []);

  const money = (value) => {
    const num = Number(value || 0);
    return `₹${Math.round(Number.isFinite(num) ? num : 0).toLocaleString('en-IN')}`;
  };

  const dateTime = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const fullDateTime = (value) => {
    if (!value) return 'Not updated yet';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return 'Not updated yet';

    return date.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const isFreshLocation = (value) => {
    if (!value) return false;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return false;
    return Date.now() - date.getTime() <= 2 * 60 * 1000;
  };

  const safeArray = (value) => {
    if (Array.isArray(value)) return value;

    if (typeof value === 'string') {
      try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    }

    return [];
  };

  const getSafeTripOrders = (trip) => {
    if (!trip) return [];

    const source =
      trip.orders ??
      trip.trip_orders ??
      trip.tripOrders ??
      [];

    return safeArray(source);
  };

  const getRiderLatLng = (trip) => {
    const lat =
      trip?.riderLatitude ||
      trip?.rider_latitude ||
      trip?.last_latitude ||
      trip?.rider?.last_latitude ||
      '';
    const lng =
      trip?.riderLongitude ||
      trip?.rider_longitude ||
      trip?.last_longitude ||
      trip?.rider?.last_longitude ||
      '';

    if (!lat || !lng) return null;

    return {
      lat,
      lng,
    };
  };

  const getFirstPendingOrder = (trip) => {
    const orders = getSafeTripOrders(trip);

    return (
      orders.find((order) =>
        !['delivered', 'cancelled', 'failed'].includes(
          String(order?.status || '').toLowerCase()
        )
      ) ||
      orders[0] ||
      null
    );
  };

  const getMapEmbedUrl = (trip) => {
    const riderPoint = getRiderLatLng(trip);
    if (riderPoint) {
      return `https://maps.google.com/maps?q=${riderPoint.lat},${riderPoint.lng}&z=16&output=embed`;
    }

    const order = getFirstPendingOrder(trip);
    if (order?.latitude && order?.longitude) {
      return `https://maps.google.com/maps?q=${order.latitude},${order.longitude}&z=15&output=embed`;
    }

    const query = encodeURIComponent(order?.fullAddress || order?.areaKey || '');
    return query ? `https://maps.google.com/maps?q=${query}&z=15&output=embed` : '';
  };

  const getRouteUrl = (trip) => {
    const riderPoint = getRiderLatLng(trip);
    const order = getFirstPendingOrder(trip);

    const destination =
      order?.latitude && order?.longitude
        ? `${order.latitude},${order.longitude}`
        : encodeURIComponent(order?.fullAddress || order?.areaKey || '');

    if (!destination) return '';

    if (riderPoint) {
      return `https://www.google.com/maps/dir/?api=1&origin=${riderPoint.lat},${riderPoint.lng}&destination=${destination}`;
    }

    return `https://www.google.com/maps/dir/?api=1&destination=${destination}`;
  };

  const getDeliveryBoyLoginLink = () => {
    const origin =
      typeof window !== 'undefined' && window.location?.origin
        ? window.location.origin
        : 'https://vakra.co.in';

    const cleanVendorType = String(
      vendorType || externalVendorType || 'fastfood'
    ).toLowerCase();

    return `${origin}/delivery-boy?vendor=${vendorId || ''}&vendorType=${encodeURIComponent(
      cleanVendorType
    )}`;
  };

  const copyDeliveryBoyLoginLink = async () => {
    const link = getDeliveryBoyLoginLink();

    if (!vendorId) {
      showMessage('Vendor details load hone ke baad link copy hoga.', 'error');
      return;
    }

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(link);
      } else {
        const input = document.createElement('input');
        input.value = link;
        document.body.appendChild(input);
        input.select();
        document.execCommand('copy');
        input.remove();
      }

      showMessage('Delivery boy login link copied.');
    } catch {
      showMessage('Link copy failed. Please copy manually.', 'error');
    }
  };

  const shareDeliveryBoyOnWhatsApp = (rider = null) => {
    if (!vendorId) {
      showMessage('Vendor details load hone ke baad WhatsApp share hoga.', 'error');
      return;
    }

    const link = getDeliveryBoyLoginLink();
    const riderLine = rider?.name
      ? `Delivery Boy: ${rider.name}%0AMobile: ${rider.mobile || ''}%0A`
      : '';

    const text = `Delivery Boy Login%0A${riderLine}Link: ${encodeURIComponent(link)}%0A%0AIs link ko open karke mobile number aur PIN se login karein.`;
    window.open(`https://wa.me/?text=${text}`, '_blank', 'noopener,noreferrer');
  };

  const renderRiderLinkActions = (rider = null) => (
    <div className="dd-rider-link-actions">
      <button
        type="button"
        className="copy"
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          copyDeliveryBoyLoginLink();
        }}
      >
        Copy Link
      </button>

      <button
        type="button"
        className="whatsapp"
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          shareDeliveryBoyOnWhatsApp(rider);
        }}
      >
        WhatsApp
      </button>
    </div>
  );

  const parseProducts = (order) => {
    try {
      if (Array.isArray(order?.productsParsed)) return order.productsParsed;
      if (Array.isArray(order?.products)) return order.products;
      return order?.products ? JSON.parse(order.products) : [];
    } catch {
      return [];
    }
  };

  const normalizeMode = (order) => {
    const raw = String(order?.order_mode || order?.orderMode || '').toLowerCase();
    if (raw === 'delivery') return 'delivery';
    if (raw === 'takeaway' || raw === 'pickup') return 'takeaway';
    if (raw === 'dine_in' || raw === 'dine-in') return 'dine_in';
    return raw || 'delivery';
  };

  const normalizeStatus = (order) => {
    const raw = String(order?.displayStatus || order?.display_status || order?.status || '').toLowerCase();
    if (raw === 'pending' || raw === 'new') return 'new';
    if (raw === 'accepted') return 'accepted';
    if (raw === 'preparing' || raw === 'kitchen') return 'preparing';
    if (raw === 'ready') return 'ready';
    if (raw === 'done' || raw === 'completed') return 'done';
    return raw || 'new';
  };

  const getAddress = (order) =>
    [
      order?.address_line1,
      order?.address_line2,
      order?.landmark,
      order?.city,
      order?.state,
      order?.pincode,
    ]
      .filter(Boolean)
      .join(', ');

  const getArea = (order) =>
    String(
      order?.area ||
        order?.delivery_area ||
        order?.landmark ||
        order?.city ||
        order?.pincode ||
        'Unknown Area'
    ).trim();

  const normalizeReadyOrder = (order) => {
    const products = parseProducts(order);
    const total = Number(order?.final_total || order?.display_total || order?.total_price || 0);

    return {
      ...order,
      id: order?.id || order?.order_id,
      orderId: order?.order_id || order?.orderId || order?.id,
      tripOrderId:
        order?.trip_order_id ||
        order?.tripOrderId ||
        order?.delivery_trip_order_id ||
        order?.id ||
        null,
      riderId: order?.rider_id || order?.riderId || null,
      displayStatus: normalizeStatus(order),
      displayMode: normalizeMode(order),
      customerName:
        order?.customerName ||
        order?.customer_name ||
        order?.delivery_details?.customer_name ||
        order?.name ||
        'Customer',
      customerMobile:
        order?.customerMobile ||
        order?.customer_mobile ||
        order?.customer_number ||
        order?.delivery_details?.customer_mobile ||
        '',
      fullAddress: order?.fullAddress || order?.full_address || getAddress(order),
      areaKey: getArea(order),
      productsParsed: products,
      itemCount: products.reduce((sum, item) => sum + Number(item?.quantity || item?.qty || 1), 0),
      displayTotal: Number.isFinite(total) ? total : 0,
      tokenNumber:
        order?.token_number ||
        order?.redeem_token ||
        order?.redeem?.token ||
        order?.token ||
        order?.redeem_code ||
        '',
      paymentStatus: String(order?.payment_status || order?.paymentStatus || 'unpaid').toLowerCase(),
      deliveryDistanceKm: Number(order?.delivery_distance_km || order?.distance_km || 0),
      latitude: order?.latitude || order?.customer_latitude || order?.delivery_latitude,
      longitude: order?.longitude || order?.customer_longitude || order?.delivery_longitude,
    };
  };

  const normalizeRider = (rider) => ({
    ...rider,
    id: rider?.id || rider?.rider_id,
    name: rider?.name || rider?.rider_name || 'Delivery Boy',
    mobile: rider?.mobile || rider?.rider_mobile || rider?.phone || '',
    vehicleType: rider?.vehicle_type || rider?.vehicleType || 'bike',
    vehicleNumber: rider?.vehicle_number || rider?.vehicleNumber || '',
    serviceArea: rider?.service_area || rider?.serviceArea || '',
    status: String(rider?.status || rider?.rider_status || 'available').toLowerCase(),
    activeOrders: Number(rider?.active_orders || rider?.activeOrders || 0),
    deliveredToday: Number(rider?.delivered_today || rider?.deliveredToday || 0),
    deliveredTotal: Number(rider?.delivered_total || rider?.deliveredTotal || 0),
    totalTrips: Number(rider?.total_trips || rider?.totalTrips || 0),
    lastLatitude: rider?.last_latitude || rider?.lastLatitude || null,
    lastLongitude: rider?.last_longitude || rider?.lastLongitude || null,
    lastLocationAt: rider?.last_location_at || rider?.lastLocationAt || null,
  });

  const normalizeTrip = (trip) => {
    const safeTrip = trip || {};
    const safeOrders = getSafeTripOrders(safeTrip);

    return {
      ...safeTrip,
      id: safeTrip?.id || safeTrip?.trip_id,
      tripNo:
        safeTrip?.trip_no ||
        safeTrip?.tripNo ||
        `TRP-${safeTrip?.id || safeTrip?.trip_id || ''}`,
      riderId: safeTrip?.rider_id || safeTrip?.riderId,
      riderName:
        safeTrip?.rider_name ||
        safeTrip?.riderName ||
        safeTrip?.rider?.name ||
        'Rider',
      riderMobile:
        safeTrip?.rider_mobile ||
        safeTrip?.riderMobile ||
        safeTrip?.rider?.mobile ||
        '',
      riderLatitude:
        safeTrip?.rider_latitude ||
        safeTrip?.riderLatitude ||
        safeTrip?.last_latitude ||
        safeTrip?.rider?.last_latitude ||
        null,
      riderLongitude:
        safeTrip?.rider_longitude ||
        safeTrip?.riderLongitude ||
        safeTrip?.last_longitude ||
        safeTrip?.rider?.last_longitude ||
        null,
      riderLastLocationAt:
        safeTrip?.rider_last_location_at ||
        safeTrip?.riderLastLocationAt ||
        safeTrip?.last_location_at ||
        safeTrip?.rider?.last_location_at ||
        null,
      status: String(safeTrip?.status || safeTrip?.trip_status || 'assigned').toLowerCase(),
      orders: safeOrders.map(normalizeReadyOrder),
      totalOrders: Number(
        safeTrip?.total_orders ||
          safeTrip?.totalOrders ||
          safeOrders.length ||
          0
      ),
      totalDistanceKm: Number(safeTrip?.total_distance_km || safeTrip?.totalDistanceKm || 0),
      estimatedMinutes: Number(safeTrip?.estimated_minutes || safeTrip?.estimatedMinutes || 0),
      createdAt: safeTrip?.created_at || safeTrip?.createdAt,
    };
  };

  const selectedRider = useMemo(
    () => riders.find((rider) => String(rider.id) === String(selectedRiderId)) || null,
    [riders, selectedRiderId]
  );

  const selectedOrders = useMemo(
    () => readyOrders.filter((order) => selectedOrderIds.includes(Number(order.id))),
    [readyOrders, selectedOrderIds]
  );

  const areaGroups = useMemo(() => {
    const groups = {};

    readyOrders.forEach((order) => {
      const key = order.areaKey || 'Unknown Area';
      if (!groups[key]) {
        groups[key] = { area: key, orders: [], total: 0, distance: 0 };
      }

      groups[key].orders.push(order);
      groups[key].total += order.displayTotal;
      groups[key].distance += Number(order.deliveryDistanceKm || 0);
    });

    return Object.values(groups).sort((a, b) => b.orders.length - a.orders.length);
  }, [readyOrders]);

  const filteredReadyOrders = useMemo(() => {
    const q = search.trim().toLowerCase();

    return readyOrders.filter((order) => {
      const areaOk = selectedArea === 'all' || order.areaKey === selectedArea;

      const text = [
        order.id,
        order.customerName,
        order.customerMobile,
        order.fullAddress,
        order.areaKey,
        order.tokenNumber,
        ...(order.productsParsed || []).map((item) => item?.name || item?.item_name),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return areaOk && (!q || text.includes(q));
    });
  }, [readyOrders, selectedArea, search]);

  const stats = useMemo(() => {
    const availableRiders = riders.filter((rider) => rider.status === 'available').length;
    const onDelivery = riders.filter((rider) => ['on_delivery', 'busy'].includes(rider.status)).length;

    return {
      readyOrders: readyOrders.length,
      availableRiders,
      onDelivery,
      activeTrips: activeTrips.filter((trip) => trip.status !== 'delivered').length,
      selected: selectedOrderIds.length,
    };
  }, [readyOrders, riders, activeTrips, selectedOrderIds]);

  const activeTrackingTrip = useMemo(() => {
    const safeTrips = Array.isArray(activeTrips) ? activeTrips : [];

    if (!safeTrips.length) return null;

    if (trackingTripId) {
      const selected = safeTrips.find(
        (trip) => String(trip?.id) === String(trackingTripId)
      );

      if (selected) return selected;
    }

    return safeTrips[0] || null;
  }, [activeTrips, trackingTripId]);

  const riderRecordList = useMemo(() => {
    const recordMap = new Map();

    riders.forEach((rider) => {
      recordMap.set(String(rider.id), {
        ...rider,
        deliveredToday: Number(rider.deliveredToday || 0),
        deliveredTotal: Number(rider.deliveredTotal || 0),
        totalTrips: Number(rider.totalTrips || 0),
        activeOrders: Number(rider.activeOrders || 0),
      });
    });

    riderRecords.forEach((record) => {
      const id = String(record.rider_id || record.riderId || record.id || '');
      if (!id) return;

      const existing = recordMap.get(id) || {};
      recordMap.set(id, {
        ...existing,
        ...record,
        id,
        name: record.rider_name || record.riderName || record.name || existing.name || 'Delivery Boy',
        mobile: record.rider_mobile || record.riderMobile || record.mobile || existing.mobile || '',
        deliveredToday: Number(record.delivered_today || record.deliveredToday || existing.deliveredToday || 0),
        deliveredTotal: Number(record.delivered_total || record.deliveredTotal || existing.deliveredTotal || 0),
        totalTrips: Number(record.total_trips || record.totalTrips || existing.totalTrips || 0),
        activeOrders: Number(record.active_orders || record.activeOrders || existing.activeOrders || 0),
      });
    });

    activeTrips.forEach((trip) => {
      const id = String(trip.riderId || '');
      if (!id) return;

      const existing = recordMap.get(id) || {
        id,
        name: trip.riderName,
        mobile: trip.riderMobile,
      };

      const deliveredInTrip = (trip.orders || []).filter(
        (order) => String(order.status || '').toLowerCase() === 'delivered'
      ).length;

      recordMap.set(id, {
        ...existing,
        deliveredTotal: Number(existing.deliveredTotal || 0) + deliveredInTrip,
        activeOrders:
          Number(existing.activeOrders || 0) +
          (trip.status !== 'delivered' ? Number(trip.totalOrders || trip.orders?.length || 0) : 0),
      });
    });

    return Array.from(recordMap.values()).sort(
      (a, b) => Number(b.deliveredToday || b.deliveredTotal || 0) - Number(a.deliveredToday || a.deliveredTotal || 0)
    );
  }, [riders, riderRecords, activeTrips]);

  const selectedRiderTrips = useMemo(() => {
    if (!selectedRiderRecord?.id) return [];

    return tripHistory
      .filter(
        (trip) =>
          String(trip.riderId || trip.rider_id || '') ===
          String(selectedRiderRecord.id)
      )
      .sort((a, b) => {
        const bTime = new Date(b.createdAt || b.created_at || 0).getTime();
        const aTime = new Date(a.createdAt || a.created_at || 0).getTime();
        return bTime - aTime;
      });
  }, [tripHistory, selectedRiderRecord]);

  const selectedRiderDeliveredOrders = useMemo(() => {
    return selectedRiderTrips.reduce((sum, trip) => {
      const delivered = (trip.orders || []).filter(
        (order) => String(order.status || '').toLowerCase() === 'delivered'
      ).length;

      return sum + delivered;
    }, 0);
  }, [selectedRiderTrips]);

  const tripListForDisplay = useMemo(() => {
    const map = new Map();

    [...activeTrips, ...tripHistory].forEach((trip) => {
      if (!trip?.id) return;

      const id = String(trip.id);
      const existing = map.get(id);

      map.set(id, {
        ...(existing || {}),
        ...trip,
        orders:
          Array.isArray(trip.orders) && trip.orders.length
            ? trip.orders
            : existing?.orders || [],
      });
    });

    return Array.from(map.values()).sort((a, b) => {
      const bTime = new Date(b.createdAt || b.created_at || 0).getTime();
      const aTime = new Date(a.createdAt || a.created_at || 0).getTime();
      return bTime - aTime;
    });
  }, [activeTrips, tripHistory]);

  const fetchVendorDetails = async () => {
    if (externalVendorId) {
      setVendorId(externalVendorId);
      setVendorType(String(externalVendorType || 'fastfood').toLowerCase());
      setLoading(false);
      return;
    }

    const token = getToken();

    if (!token) {
      showMessage('Please login first.', 'error');
      setLoading(false);
      return;
    }

    try {
      const res = await axios.get(`${STANDARD_ORDERS_API}/get-vendor-id`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setVendorId(res.data?.vendorId || res.data?.vendor_id || null);
      setVendorType(String(res.data?.vendorType || res.data?.vendor_type || 'fastfood').toLowerCase());
    } catch (err) {
      showMessage(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message ||
          'Vendor details load failed.',
        'error'
      );
      setLoading(false);
    }
  };

  const fetchReadyOrdersFallback = async () => {
    const token = getToken();
    const cleanVendorType = String(
      vendorType || externalVendorType || 'fastfood'
    ).toLowerCase();

    const fallbackUrl =
      cleanVendorType === 'grocery'
        ? `${GROCERY_ORDERS_API}/today-orders`
        : `${STANDARD_ORDERS_API}/today-orders?userdb=true`;

    const res = await axios.get(fallbackUrl, {
      headers: { Authorization: `Bearer ${token}` },
    });

    const orders = Array.isArray(res.data?.orders) ? res.data.orders : [];
    const deliveryReady = orders
      .map(normalizeReadyOrder)
      .filter(
        (order) =>
          order.displayMode === 'delivery' &&
          order.displayStatus === 'ready'
      );

    setReadyOrders(deliveryReady);
  };

  const fetchDeliveryData = async ({ silent = false } = {}) => {
    const token = getToken();
    if (!token || !vendorId) return;

    try {
      if (!silent) setLoading(true);

      const [ordersRes, ridersRes, tripsRes, recordsRes, historyRes] = await Promise.allSettled([
        axios.get(`${DELIVERY_API}/ready-orders`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${DELIVERY_API}/riders`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${DELIVERY_API}/trips/active`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${DELIVERY_API}/rider-records`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${DELIVERY_API}/trips/history?limit=300`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      if (ordersRes.status === 'fulfilled') {
        const orders = Array.isArray(ordersRes.value?.data?.orders)
          ? ordersRes.value.data.orders
          : [];
        setReadyOrders(orders.map(normalizeReadyOrder));
      } else {
        await fetchReadyOrdersFallback();
      }

      if (ridersRes.status === 'fulfilled') {
        const list = Array.isArray(ridersRes.value?.data?.riders)
          ? ridersRes.value.data.riders
          : [];
        setRiders(list.map(normalizeRider));
      } else if (!silent) {
        setRiders([]);
      }

      if (tripsRes.status === 'fulfilled') {
        const trips = Array.isArray(tripsRes.value?.data?.trips)
          ? tripsRes.value.data.trips
          : [];
        setActiveTrips(trips.map(normalizeTrip));
      } else if (!silent) {
        setActiveTrips([]);
      }

      if (recordsRes.status === 'fulfilled') {
        const records = Array.isArray(recordsRes.value?.data?.records)
          ? recordsRes.value.data.records
          : Array.isArray(recordsRes.value?.data?.riders)
            ? recordsRes.value.data.riders
            : [];
        setRiderRecords(records);
      } else if (!silent) {
        setRiderRecords([]);
      }

      if (historyRes.status === 'fulfilled') {
        const history = Array.isArray(historyRes.value?.data?.trips)
          ? historyRes.value.data.trips
          : [];
        setTripHistory(history.map(normalizeTrip));
      } else if (!silent) {
        setTripHistory([]);
      }
    } catch (err) {
      if (!silent) {
        showMessage(
          err?.response?.data?.message ||
            err?.response?.data?.error ||
            err?.message ||
            'Delivery data load failed.',
          'error'
        );
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVendorDetails();
  }, [externalVendorId, externalVendorType]);

  useEffect(() => {
    if (!vendorId) return;

    fetchDeliveryData({ silent: false });

    const timer = window.setInterval(() => {
      fetchDeliveryData({ silent: true });
    }, 12000);

    return () => window.clearInterval(timer);
  }, [vendorId, vendorType]);

  const toggleOrderSelect = (orderId) => {
    const id = Number(orderId);

    setSelectedOrderIds((prev) =>
      prev.includes(id)
        ? prev.filter((item) => item !== id)
        : [...prev, id]
    );
  };

  const selectAreaOrders = (area) => {
    const ids = readyOrders
      .filter((order) => order.areaKey === area)
      .map((order) => Number(order.id));

    const allSelected = ids.length && ids.every((id) => selectedOrderIds.includes(id));

    setSelectedOrderIds((prev) =>
      allSelected
        ? prev.filter((id) => !ids.includes(id))
        : Array.from(new Set([...prev, ...ids]))
    );
  };

  const clearSelection = () => {
    setSelectedOrderIds([]);
    setSelectedRiderId('');
  };

  const createRider = async (event) => {
    event.preventDefault();

    const token = getToken();
    const name = riderForm.name.trim();
    const mobile = riderForm.mobile.replace(/\D/g, '').slice(0, 10);
    const pin = riderForm.pin.trim();

    if (!name || mobile.length !== 10 || !pin) {
      showMessage('Name, 10 digit mobile and PIN required.', 'error');
      return;
    }

    try {
      setActionLoading(true);

      const payload = {
        name,
        mobile,
        pin,
        vehicleType: riderForm.vehicleType,
        vehicleNumber: riderForm.vehicleNumber.trim(),
        serviceArea: riderForm.serviceArea.trim(),
        status: riderForm.status,
        notes: riderForm.notes.trim(),
      };

      const res = await axios.post(`${DELIVERY_API}/riders`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const newRider = normalizeRider(res.data?.rider || payload);
      setRiders((prev) => [newRider, ...prev]);
      setRiderForm(emptyRiderForm);
      setShowRiderForm(false);
      showMessage('Delivery boy account created. Login link copy/share karke rider ko bhejein.');
      fetchDeliveryData({ silent: true });
    } catch (err) {
      showMessage(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message ||
          'Delivery boy create failed.',
        'error'
      );
    } finally {
      setActionLoading(false);
    }
  };

  const assignTrip = async () => {
    const token = getToken();

    if (!selectedOrderIds.length) {
      showMessage('Assign karne ke liye orders select karein.', 'error');
      return;
    }

    if (!selectedRiderId) {
      showMessage('Delivery boy select karein.', 'error');
      return;
    }

    try {
      setActionLoading(true);

      const payload = {
        riderId: selectedRiderId,
        orderIds: selectedOrderIds,
        tokenVerificationRequired: true,
      };

      const res = await axios.post(`${DELIVERY_API}/assign-trip`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const trip = normalizeTrip(res.data?.trip || {
        id: Date.now(),
        trip_no: `TRP-${Date.now()}`,
        rider_id: selectedRiderId,
        rider_name: selectedRider?.name,
        rider_mobile: selectedRider?.mobile,
        status: 'assigned',
        orders: selectedOrders,
        total_orders: selectedOrders.length,
      });

      setActiveTrips((prev) => [trip, ...prev]);
      setReadyOrders((prev) =>
        prev.filter((order) => !selectedOrderIds.includes(Number(order.id)))
      );
      setTrackingTripId(trip.id);
      setActiveTab('dispatch');
      clearSelection();
      showMessage(`${trip.totalOrders || selectedOrderIds.length} orders assigned to ${trip.riderName}.`);
    } catch (err) {
      showMessage(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message ||
          'Trip assign failed.',
        'error'
      );
    } finally {
      setActionLoading(false);
    }
  };

  const updateRiderStatus = async (riderId, status) => {
    const token = getToken();

    try {
      setActionLoading(true);

      await axios.patch(
        `${DELIVERY_API}/riders/${riderId}/status`,
        { status },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setRiders((prev) =>
        prev.map((rider) =>
          String(rider.id) === String(riderId) ? { ...rider, status } : rider
        )
      );
      showMessage('Rider status updated.');
    } catch (err) {
      showMessage(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message ||
          'Status update failed.',
        'error'
      );
    } finally {
      setActionLoading(false);
    }
  };

  const openResetPinModal = (rider) => {
    setResetPinRider(rider);
    setResetPinValue('');
  };

  const closeResetPinModal = () => {
    setResetPinRider(null);
    setResetPinValue('');
  };

  const resetRiderPin = async (event) => {
    event.preventDefault();

    if (!resetPinRider?.id) return;

    const newPin = String(resetPinValue || '').trim();

    if (!/^\d{4,6}$/.test(newPin)) {
      showMessage('New PIN 4 se 6 digit ka hona chahiye.', 'error');
      return;
    }

    try {
      setActionLoading(true);

      await axios.patch(
        `${DELIVERY_API}/riders/${resetPinRider.id}/reset-pin`,
        { newPin },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );

      showMessage(`${resetPinRider.name} ka PIN reset ho gaya.`);
      closeResetPinModal();
    } catch (err) {
      showMessage(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message ||
          'PIN reset failed.',
        'error'
      );
    } finally {
      setActionLoading(false);
    }
  };

  const deleteRider = async () => {
    if (!deleteRiderTarget?.id) return;

    try {
      setActionLoading(true);

      await axios.delete(`${DELIVERY_API}/riders/${deleteRiderTarget.id}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });

      setRiders((prev) =>
        prev.filter((rider) => String(rider.id) !== String(deleteRiderTarget.id))
      );

      showMessage(`${deleteRiderTarget.name} ka account delete ho gaya.`);
      setDeleteRiderTarget(null);
      await fetchDeliveryData({ silent: true });
    } catch (err) {
      showMessage(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message ||
          'Delivery boy delete failed.',
        'error'
      );
    } finally {
      setActionLoading(false);
    }
  };

  const openReassignModal = (order, trip) => {
    setReassignOrder({
      ...order,
      tripId: trip?.id || order?.trip_id || order?.tripId || null,
      tripNo: trip?.tripNo || trip?.trip_no || '',
      oldRiderId:
        order?.riderId ||
        order?.rider_id ||
        trip?.riderId ||
        trip?.rider_id ||
        null,
      oldRiderName: trip?.riderName || trip?.rider_name || 'Current Rider',
    });
    setReassignRiderId('');
    setReassignReason('');
  };

  const closeReassignModal = () => {
    setReassignOrder(null);
    setReassignRiderId('');
    setReassignReason('');
  };

  const reassignTripOrder = async (event) => {
    event.preventDefault();

    if (!reassignOrder?.tripOrderId) {
      showMessage('Trip order ID missing hai. Refresh karke dobara try karein.', 'error');
      return;
    }

    if (!reassignRiderId) {
      showMessage('New delivery boy select karein.', 'error');
      return;
    }

    try {
      setActionLoading(true);

      const res = await axios.patch(
        `${DELIVERY_API}/trip-orders/${reassignOrder.tripOrderId}/reassign`,
        {
          newRiderId: reassignRiderId,
          reason: reassignReason.trim() || 'Vendor reassigned delivery order',
        },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );

      showMessage(res?.data?.message || 'Delivery order reassigned successfully.');
      closeReassignModal();
      await fetchDeliveryData({ silent: false });
    } catch (err) {
      showMessage(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message ||
          'Order reassign failed.',
        'error'
      );
    } finally {
      setActionLoading(false);
    }
  };

  const getReassignableRiders = () =>
    riders.filter(
      (rider) =>
        String(rider.status || '').toLowerCase() !== 'inactive' &&
        String(rider.id) !== String(reassignOrder?.oldRiderId || '')
    );

  const renderToast = () =>
    message ? (
      <div className={`dd-toast ${messageType}`}>
        <span>{messageType === 'error' ? '⚠️' : '✅'}</span>
        <p>{message}</p>
        <button type="button" onClick={() => setMessage('')}>×</button>
      </div>
    ) : null;

  const renderHeader = () => (
    <header className="dd-header">
      <div>
        {embedded && typeof onBack === 'function' ? (
          <button
            type="button"
            className="dd-back-orders-btn"
            onClick={onBack}
          >
            ← Orders
          </button>
        ) : null}

        <span className="dd-kicker">Delivery Dispatch</span>
        <h1>Delivery Management</h1>
        <p>Ready delivery orders ko area wise group karke ek rider ko multiple orders assign karein.</p>
      </div>

      <div className="dd-header-actions">
        <button type="button" className="dd-secondary-btn" onClick={() => fetchDeliveryData({ silent: false })}>
          ↻ Refresh
        </button>

        <button type="button" className="dd-secondary-btn" onClick={copyDeliveryBoyLoginLink}>
          🔗 Copy Login Link
        </button>

        <button type="button" className="dd-secondary-btn whatsapp" onClick={() => shareDeliveryBoyOnWhatsApp()}>
          WhatsApp Link
        </button>

        <button type="button" className="dd-primary-btn" onClick={() => setShowRiderForm(true)}>
          + Add Delivery Boy
        </button>
      </div>
    </header>
  );

  const renderStats = () => (
    <section className="dd-stats">
      <div className="dd-stat-card blue"><span>📦</span><div><strong>{stats.readyOrders}</strong><small>Ready Delivery</small></div></div>
      <div className="dd-stat-card green"><span>🛵</span><div><strong>{stats.availableRiders}</strong><small>Available Riders</small></div></div>
      <div className="dd-stat-card orange"><span>🚚</span><div><strong>{stats.onDelivery}</strong><small>On Delivery</small></div></div>
      <div className="dd-stat-card purple"><span>🗺️</span><div><strong>{stats.activeTrips}</strong><small>Active Trips</small></div></div>
    </section>
  );

  const renderTabs = () => (
    <nav className="dd-tabs">
      <button type="button" className={activeTab === 'dispatch' ? 'active' : ''} onClick={() => setActiveTab('dispatch')}>Dispatch</button>
      <button type="button" className={activeTab === 'riders' ? 'active' : ''} onClick={() => setActiveTab('riders')}>Riders</button>
      <button type="button" className={activeTab === 'tracking' ? 'active' : ''} onClick={() => setActiveTab('tracking')}>Tracking</button>
      <button type="button" className={activeTab === 'trips' ? 'active' : ''} onClick={() => setActiveTab('trips')}>Trips</button>
    </nav>
  );

  const renderAreaPanel = () => (
    <section className="dd-panel dd-area-panel">
      <div className="dd-panel-head">
        <div>
          <h2>Ready Orders</h2>
          <p>Area wise group, nearest orders select karke rider ko assign karein.</p>
        </div>
        <button type="button" onClick={clearSelection} disabled={!selectedOrderIds.length}>Clear</button>
      </div>

      <div className="dd-search-row">
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search order, customer, token, area" />
        <select value={selectedArea} onChange={(e) => setSelectedArea(e.target.value)}>
          <option value="all">All Areas</option>
          {areaGroups.map((group) => (
            <option key={group.area} value={group.area}>{group.area} ({group.orders.length})</option>
          ))}
        </select>
      </div>

      <div className="dd-area-list">
        {areaGroups.length ? areaGroups.map((group) => (
          <div className="dd-area-group" key={group.area}>
            <div className="dd-area-title-row">
              <label>
                <input
                  type="checkbox"
                  checked={group.orders.length > 0 && group.orders.every((order) => selectedOrderIds.includes(Number(order.id)))}
                  onChange={() => selectAreaOrders(group.area)}
                />
                <span>{group.area}</span>
              </label>
              <b>{group.orders.length} Orders</b>
            </div>

            <div className="dd-order-list">
              {group.orders
                .filter((order) => filteredReadyOrders.some((item) => Number(item.id) === Number(order.id)))
                .map((order) => (
                  <label className="dd-ready-order" key={order.id}>
                    <input type="checkbox" checked={selectedOrderIds.includes(Number(order.id))} onChange={() => toggleOrderSelect(order.id)} />

                    <div>
                      <strong>#ORD-{order.id}</strong>
                      <span>{dateTime(order.created_at)}</span>
                    </div>

                    <div>
                      <b>{order.customerName}</b>
                      <span>{order.fullAddress || 'Address missing'}</span>
                    </div>

                    <div>
                      <small className={`dd-payment ${order.paymentStatus}`}>
                        {order.paymentStatus === 'paid' ? 'Paid' : order.paymentStatus === 'cod' ? 'COD' : order.paymentStatus === 'partial' ? 'Partial' : 'Credit'}
                      </small>
                      <span>{money(order.displayTotal)}</span>
                    </div>

                    <div>
                      <small className="dd-token">Token: {order.tokenNumber || 'N/A'}</small>
                      <em>Delivery Ready</em>
                    </div>
                  </label>
                ))}
            </div>
          </div>
        )) : (
          <div className="dd-empty"><strong>No ready delivery orders</strong><p>Orders page me delivery order Ready mark hote hi yahan dikhega.</p></div>
        )}
      </div>
    </section>
  );

  const renderAssignPanel = () => (
    <section className="dd-panel dd-assign-panel">
      <div className="dd-panel-head">
        <div><h2>Assign Rider</h2><p>Available rider choose karein.</p></div>
      </div>

      <div className="dd-rider-select-list">
        {riders.length ? riders.map((rider) => (
          <label className={`dd-rider-option ${String(selectedRiderId) === String(rider.id) ? 'selected' : ''}`} key={rider.id}>
            <input
              type="radio"
              name="selectedRider"
              value={rider.id}
              checked={String(selectedRiderId) === String(rider.id)}
              onChange={(e) => setSelectedRiderId(e.target.value)}
              disabled={rider.status !== 'available'}
            />

            <div className="dd-rider-avatar">🛵</div>

            <div>
              <strong>{rider.name}</strong>
              <span>{rider.mobile}</span>
              <small>{rider.vehicleType} {rider.vehicleNumber ? `• ${rider.vehicleNumber}` : ''}</small>
              {renderRiderLinkActions(rider)}
            </div>

            <em className={rider.status}>{statusLabel[rider.status] || rider.status}</em>
          </label>
        )) : (
          <div className="dd-empty small"><strong>No riders added</strong><p>Add Delivery Boy button se rider account banayein.</p></div>
        )}
      </div>

      <button type="button" className="dd-assign-btn" disabled={actionLoading || !selectedOrderIds.length || !selectedRiderId} onClick={assignTrip}>
        Assign {selectedOrderIds.length || 0} Orders {selectedRider ? `to ${selectedRider.name}` : ''}
      </button>

      <div className="dd-safe-note">
        🔐 Delivery boy customer se token number lega. Token match hone ke baad hi delivery confirm hogi.
      </div>
    </section>
  );

  const renderTripPreview = () => (
    <section className="dd-panel dd-trip-preview">
      <div className="dd-panel-head">
        <div><h2>Trip Preview</h2><p>Selected orders ka short route preview.</p></div>
      </div>

      {selectedOrders.length ? (
        <>
          <div className="dd-trip-summary">
            <div><small>Orders</small><strong>{selectedOrders.length}</strong></div>
            <div><small>Total Amount</small><strong>{money(selectedOrders.reduce((sum, order) => sum + order.displayTotal, 0))}</strong></div>
            <div><small>Area</small><strong>{selectedOrders[0]?.areaKey || '-'}</strong></div>
          </div>

          <div className="dd-route-list">
            <div className="dd-route-stop shop"><span>🏪</span><div><strong>Shop Pickup</strong><small>Delivery boy order pickup karega</small></div></div>
            {selectedOrders.map((order, index) => (
              <div className="dd-route-stop" key={order.id}>
                <span>{index + 1}</span>
                <div><strong>{order.customerName}</strong><small>{order.areaKey} • Token {order.tokenNumber || 'N/A'}</small></div>
              </div>
            ))}
          </div>
        </>
      ) : (
        <div className="dd-empty small"><strong>Select orders</strong><p>Area panel se orders select karne ke baad preview dikhega.</p></div>
      )}
    </section>
  );

  const renderMiniTrackingPanel = () => (
    <section className="dd-panel dd-mini-track-panel">
      <div className="dd-panel-head">
        <div>
          <h2>Live Rider Tracking</h2>
          <p>Tracking module ko abhi temporarily pause rakha gaya hai.</p>
        </div>
      </div>

      <div className="dd-mini-track-empty">
        <strong>This facility is not available right now.</strong>
        <span>Delivery dispatch, rider assignment aur trip records ka kaam normally chalta rahega.</span>
      </div>
    </section>
  );

  const renderDispatchTab = () => (
    <div className="dd-dispatch-grid">
      {renderAreaPanel()}
      <div className="dd-right-stack">
        {renderAssignPanel()}
        {renderMiniTrackingPanel()}
        {renderTripPreview()}
      </div>
    </div>
  );

  const renderRidersTab = () => (
    <section className="dd-panel dd-riders-table-panel">
      <div className="dd-panel-head">
        <div>
          <h2>Delivery Boys</h2>
          <p>Rider account, PIN, status, area aur vehicle vendor ke control me manage karein.</p>
        </div>
        <button type="button" onClick={() => setShowRiderForm(true)}>+ Add Rider</button>
      </div>

      {renderRiderRecords()}

      <div className="dd-riders-table">
        <div className="dd-table-head">
          <span>Name</span>
          <span>Mobile</span>
          <span>Vehicle</span>
          <span>Area</span>
          <span>Status</span>
          <span>Login Link</span>
          <span>Vendor Actions</span>
        </div>

        {riders.length ? riders.map((rider) => (
          <div className="dd-table-row" key={rider.id}>
            <span>{rider.name}</span>
            <span>{rider.mobile}</span>
            <span>{rider.vehicleType} {rider.vehicleNumber || '-'}</span>
            <span>{rider.serviceArea || '-'}</span>
            <span>
              <b className={`dd-status-pill ${rider.status}`}>
                {statusLabel[rider.status] || rider.status}
              </b>
            </span>
            <span>{renderRiderLinkActions(rider)}</span>
            <span className="dd-rider-admin-actions">
              <select
                value={rider.status}
                onChange={(event) => updateRiderStatus(rider.id, event.target.value)}
              >
                <option value="available">Available</option>
                <option value="on_delivery">On Delivery</option>
                <option value="inactive">Inactive</option>
              </select>

              <button
                type="button"
                className="reset-pin"
                onClick={() => openResetPinModal(rider)}
              >
                Reset PIN
              </button>

              <button
                type="button"
                className="delete"
                onClick={() => setDeleteRiderTarget(rider)}
              >
                Delete
              </button>
            </span>
          </div>
        )) : (
          <div className="dd-empty">
            <strong>No delivery boys</strong>
            <p>Rider account banane ke baad yahan show hoga.</p>
          </div>
        )}
      </div>
    </section>
  );

  const renderRealTrackingMap = (trip) => {
    const mapUrl = getMapEmbedUrl(trip);
    const routeUrl = getRouteUrl(trip);
    const riderPoint = getRiderLatLng(trip);
    const fresh = isFreshLocation(trip?.riderLastLocationAt);

    return (
      <div className="dd-real-map-card">
        <div className="dd-real-map-head">
          <div>
            <strong>{riderPoint ? 'Real Rider Location' : 'Customer Location Preview'}</strong>
            <span>
              {riderPoint
                ? `Last update: ${fullDateTime(trip.riderLastLocationAt)}`
                : 'Delivery boy ke phone me location allow hone ke baad live rider location yahan dikhegi.'}
            </span>
          </div>

          <div className={`dd-live-dot ${fresh ? 'fresh' : 'old'}`}>
            <i></i>
            {fresh ? 'Live' : 'Waiting'}
          </div>
        </div>

        {mapUrl ? (
          <div className="dd-real-map-frame">
            <iframe
              title="Real Delivery Tracking Map"
              src={mapUrl}
              loading="lazy"
              allowFullScreen
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        ) : (
          <div className="dd-map-placeholder compact">
            <strong>Location not available</strong>
            <small>Rider location ya customer location missing hai.</small>
          </div>
        )}

        <div className="dd-live-location-grid">
          <div>
            <small>Rider Lat</small>
            <strong>{trip?.riderLatitude || '-'}</strong>
          </div>
          <div>
            <small>Rider Lng</small>
            <strong>{trip?.riderLongitude || '-'}</strong>
          </div>
          <div>
            <small>Updated</small>
            <strong>{trip?.riderLastLocationAt ? fullDateTime(trip.riderLastLocationAt) : '-'}</strong>
          </div>
        </div>

        <div className="dd-map-actions-row">
          <button type="button" onClick={() => fetchDeliveryData({ silent: false })}>
            Refresh Location
          </button>
          {routeUrl ? (
            <a href={routeUrl} target="_blank" rel="noreferrer">
              Open Full Route
            </a>
          ) : null}
        </div>
      </div>
    );
  };

  const renderRiderRecords = () => (
    <section className="dd-rider-record-panel">
      <div className="dd-panel-head">
        <div>
          <h2>Delivery Boy Records</h2>
          <p>Har rider ne kitne orders deliver kiye, kitne active hain aur kitne trips complete kiye.</p>
        </div>
      </div>

      <div className="dd-rider-record-grid">
        {riderRecordList.length ? (
          riderRecordList.map((rider) => (
            <button
              type="button"
              className="dd-rider-record-card clickable"
              key={rider.id || rider.mobile}
              onClick={() => setSelectedRiderRecord(rider)}
            >
              <div className="dd-rider-record-card-top">
                <div>
                  <strong>{rider.name}</strong>
                  <span>{rider.mobile || '-'}</span>
                </div>

                <em>View Details →</em>
              </div>

              <div className="dd-rider-record-stats">
                <div>
                  <small>Today</small>
                  <b>{Number(rider.deliveredToday || 0)}</b>
                </div>
                <div>
                  <small>Total</small>
                  <b>{Number(rider.deliveredTotal || 0)}</b>
                </div>
                <div>
                  <small>Active</small>
                  <b>{Number(rider.activeOrders || 0)}</b>
                </div>
                <div>
                  <small>Trips</small>
                  <b>{Number(rider.totalTrips || 0)}</b>
                </div>
              </div>
            </button>
          ))
        ) : (
          <div className="dd-empty small">
            <strong>No delivery record</strong>
            <p>Delivery complete hone ke baad rider wise record yahan show hoga.</p>
          </div>
        )}
      </div>
    </section>
  );

  const renderRiderRecordDetailModal = () => {
    if (!selectedRiderRecord) return null;

    const rider = selectedRiderRecord;

    return (
      <div className="dd-rider-detail-backdrop" onClick={() => setSelectedRiderRecord(null)}>
        <section
          className="dd-rider-detail-modal"
          onClick={(event) => event.stopPropagation()}
        >
          <div className="dd-rider-detail-head">
            <div>
              <small>Delivery Boy Full Record</small>
              <h2>{rider.name}</h2>
              <p>{rider.mobile || '-'}</p>
            </div>

            <button type="button" onClick={() => setSelectedRiderRecord(null)}>
              ×
            </button>
          </div>

          <div className="dd-rider-detail-profile-grid">
            <div>
              <small>Status</small>
              <strong>{statusLabel[rider.status] || rider.status || '-'}</strong>
            </div>
            <div>
              <small>Vehicle</small>
              <strong>
                {rider.vehicleType || rider.vehicle_type || '-'}
                {(rider.vehicleNumber || rider.vehicle_number)
                  ? ` • ${rider.vehicleNumber || rider.vehicle_number}`
                  : ''}
              </strong>
            </div>
            <div>
              <small>Service Area</small>
              <strong>{rider.serviceArea || rider.service_area || '-'}</strong>
            </div>
            <div>
              <small>Last Location</small>
              <strong>{fullDateTime(rider.lastLocationAt || rider.last_location_at)}</strong>
            </div>
          </div>

          <div className="dd-rider-detail-stat-grid">
            <div><small>Delivered Today</small><b>{Number(rider.deliveredToday || 0)}</b></div>
            <div><small>Total Delivered</small><b>{Number(rider.deliveredTotal || 0)}</b></div>
            <div><small>Active Orders</small><b>{Number(rider.activeOrders || 0)}</b></div>
            <div><small>Total Trips</small><b>{Number(rider.totalTrips || selectedRiderTrips.length || 0)}</b></div>
            <div><small>Loaded Delivered Orders</small><b>{selectedRiderDeliveredOrders}</b></div>
          </div>

          <div className="dd-rider-detail-trip-section">
            <div className="dd-panel-head">
              <div>
                <h2>Trip Details</h2>
                <p>Is rider ko assign hue trips aur unke orders.</p>
              </div>
            </div>

            <div className="dd-rider-detail-trip-list">
              {selectedRiderTrips.length ? (
                selectedRiderTrips.map((trip) => (
                  <article className="dd-rider-detail-trip-card" key={trip.id}>
                    <div className="dd-rider-detail-trip-head">
                      <div>
                        <strong>{trip.tripNo}</strong>
                        <span>{dateTime(trip.createdAt || trip.created_at)}</span>
                      </div>

                      <em className={`dd-status-pill ${trip.status}`}>
                        {tripStatusLabel[trip.status] || trip.status}
                      </em>
                    </div>

                    <div className="dd-rider-detail-trip-meta">
                      <span>{Number(trip.totalOrders || trip.orders?.length || 0)} Orders</span>
                      <span>{money(trip.total_amount || trip.totalAmount || 0)}</span>
                      <span>{Number(trip.totalDistanceKm || trip.total_distance_km || 0).toFixed(2)} KM</span>
                    </div>

                    <div className="dd-rider-detail-order-list">
                      {(trip.orders || []).length ? (
                        trip.orders.map((order, index) => (
                          <div className="dd-rider-detail-order-row" key={order.id || `${trip.id}-${index}`}>
                            <span>{index + 1}</span>
                            <div>
                              <strong>Order #{order.order_id || order.orderId || '-'}</strong>
                              <small>{order.customerName || order.customer_name || 'Customer'} • {order.customerMobile || order.customer_mobile || '-'}</small>
                              <small>{order.fullAddress || order.address || order.areaKey || '-'}</small>
                              <small className="dd-token">Token: {order.tokenNumber || 'N/A'}</small>
                            </div>
                            <div>
                              <b>{money(order.amount || order.displayTotal || 0)}</b>
                              <em className={`dd-status-pill ${order.status || 'assigned'}`}>
                                {order.status || 'assigned'}
                              </em>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="dd-empty small">
                          <strong>Order details not available</strong>
                          <p>Is trip ke order records backend response me nahi mile.</p>
                        </div>
                      )}
                    </div>
                  </article>
                ))
              ) : (
                <div className="dd-empty small">
                  <strong>No trip details</strong>
                  <p>Is rider ke trip records available nahi hain.</p>
                </div>
              )}
            </div>
          </div>
        </section>
      </div>
    );
  };

  const renderTrackingTimeline = (trip) => {
    const steps = [
      { key: 'assigned', label: 'Assigned' },
      { key: 'picked_up', label: 'Picked Up' },
      { key: 'out_for_delivery', label: 'Out for Delivery' },
      { key: 'delivered', label: 'Delivered' },
    ];

    const currentIndex = Math.max(0, steps.findIndex((step) => step.key === trip.status));

    return (
      <div className="dd-track-timeline">
        {steps.map((step, index) => (
          <div className={`dd-track-step ${index <= currentIndex ? 'done' : ''}`} key={step.key}>
            <span>{index <= currentIndex ? '✓' : index + 1}</span>
            <div><strong>{step.label}</strong><small>{index <= currentIndex ? 'Updated' : 'Pending'}</small></div>
          </div>
        ))}
      </div>
    );
  };

  const renderTrackingTab = () => (
    <section className="dd-panel dd-trips-history-panel">
      <div className="dd-panel-head">
        <div>
          <h2>Live Rider Tracking</h2>
          <p>Delivery tracking module abhi temporary unavailable hai.</p>
        </div>
      </div>

      <div className="dd-empty">
        <strong>This facility is not available right now.</strong>
        <p>Tracking feature ko baad me stable version ke saath enable kiya jayega. Abhi Dispatch, Riders aur Trips sections ka use karein.</p>
      </div>
    </section>
  );

  const renderTripsTab = () => (
    <section className="dd-panel dd-trips-history-panel">
      <div className="dd-panel-head">
        <div>
          <h2>Trip History</h2>
          <p>Assigned, delivered aur completed trips ka record. Pending order ko yahin se reassign karein.</p>
        </div>
      </div>

      <div className="dd-trips-history-list">
        {tripListForDisplay.length ? tripListForDisplay.map((trip) => (
          <article className="dd-history-trip-card" key={trip.id}>
            <div className="dd-history-trip-row">
              <div>
                <strong>{trip.tripNo}</strong>
                <span>{dateTime(trip.createdAt)}</span>
              </div>

              <div>
                <b>{trip.riderName}</b>
                <span>{trip.totalOrders} orders</span>
              </div>

              <div>
                <small className={`dd-status-pill ${trip.status}`}>
                  {tripStatusLabel[trip.status] || trip.status}
                </small>
              </div>

              <button
                type="button"
                onClick={() => {
                  setTrackingTripId(trip.id);
                  setActiveTab('tracking');
                }}
              >
                Tracking Status
              </button>
            </div>

            <div className="dd-trip-order-list dd-trip-order-management-list">
              {(trip.orders || []).length ? (
                trip.orders.map((order, index) => {
                  const orderStatus = String(order.status || 'assigned').toLowerCase();
                  const canReassign = !['delivered', 'cancelled', 'failed'].includes(orderStatus);

                  return (
                    <div className="dd-trip-order-row dd-trip-order-management-row" key={order.tripOrderId || order.id || `${trip.id}-${index}`}>
                      <span>{index + 1}</span>

                      <div>
                        <strong>Order #{order.orderId || order.order_id || order.id || '-'}</strong>
                        <small>{order.customerName || order.customer_name || 'Customer'} • {order.customerMobile || order.customer_mobile || '-'}</small>
                        <small>{order.fullAddress || order.address || order.areaKey || '-'}</small>
                        <small className="dd-token">Token: {order.tokenNumber || 'N/A'}</small>
                      </div>

                      <div className="dd-trip-order-actions">
                        <em className={`dd-status-pill ${orderStatus}`}>{orderStatus}</em>

                        {canReassign ? (
                          <button
                            type="button"
                            className="dd-reassign-btn"
                            onClick={() => openReassignModal(order, trip)}
                          >
                            Reassign Rider
                          </button>
                        ) : null}
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="dd-empty small">
                  <strong>No order details</strong>
                  <p>Trip order records available nahi hain.</p>
                </div>
              )}
            </div>
          </article>
        )) : (
          <div className="dd-empty">
            <strong>No trips found</strong>
            <p>Assign hone ke baad trips yahan show honge.</p>
          </div>
        )}
      </div>
    </section>
  );

  const renderResetPinModal = () =>
    resetPinRider ? (
      <div className="dd-modal-backdrop">
        <form className="dd-rider-form-modal dd-compact-admin-modal" onSubmit={resetRiderPin}>
          <div className="dd-modal-head">
            <div>
              <h2>Reset Delivery Boy PIN</h2>
              <p>{resetPinRider.name} • {resetPinRider.mobile}</p>
            </div>
            <button type="button" onClick={closeResetPinModal}>×</button>
          </div>

          <label>
            New Login PIN
            <input
              autoFocus
              value={resetPinValue}
              onChange={(event) =>
                setResetPinValue(event.target.value.replace(/\D/g, '').slice(0, 6))
              }
              placeholder="4-6 digit PIN"
            />
          </label>

          <div className="dd-modal-action-row">
            <button type="button" className="secondary" onClick={closeResetPinModal}>
              Cancel
            </button>
            <button type="submit" className="primary" disabled={actionLoading}>
              Save New PIN
            </button>
          </div>
        </form>
      </div>
    ) : null;

  const renderDeleteRiderModal = () =>
    deleteRiderTarget ? (
      <div className="dd-modal-backdrop">
        <section className="dd-rider-form-modal dd-compact-admin-modal">
          <div className="dd-modal-head">
            <div>
              <h2>Delete Delivery Boy?</h2>
              <p>{deleteRiderTarget.name} • {deleteRiderTarget.mobile}</p>
            </div>
            <button type="button" onClick={() => setDeleteRiderTarget(null)}>×</button>
          </div>

          <div className="dd-danger-note">
            Active delivery assigned hai to account delete nahi hoga. Pehle order reassign ya complete karein.
          </div>

          <div className="dd-modal-action-row">
            <button type="button" className="secondary" onClick={() => setDeleteRiderTarget(null)}>
              Cancel
            </button>
            <button type="button" className="danger" onClick={deleteRider} disabled={actionLoading}>
              Delete Account
            </button>
          </div>
        </section>
      </div>
    ) : null;

  const renderReassignModal = () =>
    reassignOrder ? (
      <div className="dd-modal-backdrop">
        <form className="dd-rider-form-modal dd-compact-admin-modal" onSubmit={reassignTripOrder}>
          <div className="dd-modal-head">
            <div>
              <h2>Reassign Delivery Order</h2>
              <p>Order #{reassignOrder.orderId || reassignOrder.order_id || '-'}</p>
            </div>
            <button type="button" onClick={closeReassignModal}>×</button>
          </div>

          <div className="dd-reassign-summary">
            <div>
              <small>Current Rider</small>
              <strong>{reassignOrder.oldRiderName || '-'}</strong>
            </div>
            <div>
              <small>Customer</small>
              <strong>{reassignOrder.customerName || reassignOrder.customer_name || 'Customer'}</strong>
            </div>
            <div>
              <small>Token Number</small>
              <strong>{reassignOrder.tokenNumber || 'N/A'}</strong>
            </div>
          </div>

          <label>
            New Delivery Boy
            <select
              value={reassignRiderId}
              onChange={(event) => setReassignRiderId(event.target.value)}
            >
              <option value="">Select rider</option>
              {getReassignableRiders().map((rider) => (
                <option key={rider.id} value={rider.id}>
                  {rider.name} • {rider.mobile} • {statusLabel[rider.status] || rider.status}
                </option>
              ))}
            </select>
          </label>

          <label>
            Reason
            <textarea
              value={reassignReason}
              onChange={(event) => setReassignReason(event.target.value)}
              placeholder="Example: Current rider delivery complete nahi kar pa raha hai"
            />
          </label>

          <div className="dd-safe-note">
            Token number same rahega. Customer ko naya token nahi dena padega.
          </div>

          <div className="dd-modal-action-row">
            <button type="button" className="secondary" onClick={closeReassignModal}>
              Cancel
            </button>
            <button type="submit" className="primary" disabled={actionLoading || !reassignRiderId}>
              Confirm Reassign
            </button>
          </div>
        </form>
      </div>
    ) : null;

  const renderRiderForm = () =>
    showRiderForm ? (
      <div className="dd-modal-backdrop">
        <form className="dd-rider-form-modal" onSubmit={createRider}>
          <div className="dd-modal-head">
            <div><h2>Create Delivery Boy Account</h2><p>Mobile + PIN se rider login karega.</p></div>
            <button type="button" onClick={() => setShowRiderForm(false)}>×</button>
          </div>

          <label>Full Name<input value={riderForm.name} onChange={(e) => setRiderForm((prev) => ({ ...prev, name: e.target.value }))} placeholder="Rider name" /></label>
          <label>Mobile Number<input value={riderForm.mobile} onChange={(e) => setRiderForm((prev) => ({ ...prev, mobile: e.target.value.replace(/\D/g, '').slice(0, 10) }))} placeholder="10 digit mobile" /></label>
          <label>Login PIN<input value={riderForm.pin} onChange={(e) => setRiderForm((prev) => ({ ...prev, pin: e.target.value.replace(/\D/g, '').slice(0, 6) }))} placeholder="4-6 digit PIN" /></label>

          <div className="dd-form-grid">
            <label>Vehicle Type<select value={riderForm.vehicleType} onChange={(e) => setRiderForm((prev) => ({ ...prev, vehicleType: e.target.value }))}><option value="bike">Bike</option><option value="scooter">Scooter</option><option value="cycle">Cycle</option><option value="walking">Walking</option></select></label>
            <label>Vehicle Number<input value={riderForm.vehicleNumber} onChange={(e) => setRiderForm((prev) => ({ ...prev, vehicleNumber: e.target.value }))} placeholder="MH 04 AB 1234" /></label>
          </div>

          <label>Service Area<input value={riderForm.serviceArea} onChange={(e) => setRiderForm((prev) => ({ ...prev, serviceArea: e.target.value }))} placeholder="Mira Road East" /></label>
          <label>Notes<textarea value={riderForm.notes} onChange={(e) => setRiderForm((prev) => ({ ...prev, notes: e.target.value }))} placeholder="Optional notes" /></label>

          <button type="submit" className="dd-save-rider-btn" disabled={actionLoading}>Save Delivery Boy</button>
        </form>
      </div>
    ) : null;

  const renderContent = () => {
    if (loading) {
      return (
        <section className="dd-loading-panel">
          <div className="dd-loader"></div>
          <strong>Loading delivery dispatch...</strong>
        </section>
      );
    }

    if (activeTab === 'riders') return renderRidersTab();
    if (activeTab === 'tracking') return renderTrackingTab();
    if (activeTab === 'trips') return renderTripsTab();

    return renderDispatchTab();
  };

  const content = (
    <main
      className={
        embedded
          ? 'delivery-dispatch-page dd-embedded-page'
          : 'page-below-navbar delivery-dispatch-page'
      }
    >
      {renderToast()}
      {renderHeader()}

      <section className="dd-login-link-card">
        <div>
          <strong>Delivery Boy Login Link</strong>
          <span>{getDeliveryBoyLoginLink()}</span>
        </div>

        <div>
          <button type="button" onClick={copyDeliveryBoyLoginLink}>
            Copy
          </button>
          <button type="button" className="whatsapp" onClick={() => shareDeliveryBoyOnWhatsApp()}>
            Share
          </button>
        </div>
      </section>

      {renderStats()}
      {renderTabs()}
      {renderContent()}
      {renderRiderForm()}
      {renderResetPinModal()}
      {renderDeleteRiderModal()}
      {renderReassignModal()}
      {renderRiderRecordDetailModal()}
    </main>
  );

  if (embedded) return content;

  return (
    <>
      <Dashboard />
      {content}
    </>
  );
};

export default DeliveryDispatch;
